// v3/config/permissions.config.ts — Permission matrices aligned with backend PermissionManager
//
// Backend PermissionManager write access:
//   SALES_USER → NEW, DRAFT, APPROVED, PENDING_VERIFICATION, REJECTED
//   BACK_OFFICE_USER → SUBMITTED, SOFT_APPROVED, APPROVED, INVOICE_COLLECTED, MANDATED, SIGNED, REJECTED, PENDING_VERIFICATION, VERIFICATION_COMPLETE
//   FIELD_USER → PENDING_VERIFICATION
//   MANAGER → ALL (not used in frontend — Cognito uses ADMIN/SALES_MANAGER instead)
//   ADMIN → NOT in writeUser (view only)
//   SALES_MANAGER → NOT in writeUser (view only)

import { Action, ApplicationState, ProductType, Role } from '../shared/types/domain';
import type { DefaultPermissions, FeatureAccessConfig, PermissionMatrix } from './types';

// ─── Consumer Durables Loan Permissions ──────────────────────────────────────

const CONSUMER_DURABLES_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [Action.CREATE_APPLICATION, Action.EDIT_APPLICATION, Action.SAVE_APPLICATION, Action.SUBMIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.UPLOAD_DOCUMENT],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.INVOICE_COLLECTED, Action.REJECT_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN, Action.REJECT_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE, Action.REJECT_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.MANAGER]: {
    [ApplicationState.DRAFT]: [Action.CREATE_APPLICATION, Action.EDIT_APPLICATION, Action.SAVE_APPLICATION, Action.SUBMIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.INVOICE_COLLECTED, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN, Action.REJECT_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE, Action.REJECT_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
};

// ─── Standard Loan Permissions (Personal / Business / Group) ─────────────────
// These include PENDING_VERIFICATION and VERIFICATION_COMPLETE states

const STANDARD_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [Action.CREATE_APPLICATION, Action.EDIT_APPLICATION, Action.SAVE_APPLICATION, Action.SUBMIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.UPLOAD_DOCUMENT],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.START_VERIFICATION, Action.REJECT_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN, Action.REJECT_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE, Action.REJECT_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.MANAGER]: {
    [ApplicationState.DRAFT]: [Action.CREATE_APPLICATION, Action.EDIT_APPLICATION, Action.SAVE_APPLICATION, Action.SUBMIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SOFT_APPROVED]: [Action.VIEW_APPLICATION, Action.START_VERIFICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.INVOICE_COLLECTED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN, Action.REJECT_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE, Action.REJECT_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
};

const PERSONAL_LOAN_PERMISSIONS = STANDARD_LOAN_PERMISSIONS;
const BUSINESS_LOAN_PERMISSIONS = STANDARD_LOAN_PERMISSIONS;

// ─── Group Loan Permissions ──────────────────────────────────────────────────
// GL negotiation journey: BO proposes terms in CREDIT_REVIEW (or sends back for
// edits / rejects); sales accepts or counters in SALES_REVIEW (both submit); BO
// soft-approves from SUBMITTED and sanctions at VERIFICATION_COMPLETE (as PL).
// Mirrors backend PermissionManager write sets: sales owns DRAFT + SALES_REVIEW,
// BO owns CREDIT_REVIEW/SUBMITTED/verification states.

const GROUP_LOAN_PERMISSIONS: PermissionMatrix = {
  [Role.SALES_USER]: {
    [ApplicationState.DRAFT]: [Action.CREATE_APPLICATION, Action.EDIT_APPLICATION, Action.SAVE_APPLICATION, Action.SUBMIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.CREDIT_REVIEW]: [Action.VIEW_APPLICATION],
    [ApplicationState.SALES_REVIEW]: [Action.VIEW_APPLICATION, Action.ACCEPT_TERMS, Action.COUNTER_TERMS, Action.REJECT_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.BACK_OFFICE_USER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION, Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.CREDIT_REVIEW]: [Action.VIEW_APPLICATION, Action.PROPOSE_TERMS, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SALES_REVIEW]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN, Action.REJECT_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE, Action.REJECT_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.SALES_MANAGER]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.CREDIT_REVIEW]: [Action.VIEW_APPLICATION],
    [ApplicationState.SALES_REVIEW]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.ADMIN]: {
    [ApplicationState.DRAFT]: [Action.VIEW_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION],
    [ApplicationState.CREDIT_REVIEW]: [Action.VIEW_APPLICATION],
    [ApplicationState.SALES_REVIEW]: [Action.VIEW_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
  [Role.MANAGER]: {
    [ApplicationState.DRAFT]: [Action.CREATE_APPLICATION, Action.EDIT_APPLICATION, Action.SAVE_APPLICATION, Action.SUBMIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SUBMITTED]: [Action.VIEW_APPLICATION, Action.SOFT_APPROVE, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.PENDING_VERIFICATION]: [Action.VIEW_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.VERIFICATION_COMPLETE]: [Action.VIEW_APPLICATION, Action.SANCTION, Action.REJECT_APPLICATION],
    [ApplicationState.CREDIT_REVIEW]: [Action.VIEW_APPLICATION, Action.PROPOSE_TERMS, Action.REQUEST_EDIT_APPLICATION, Action.REJECT_APPLICATION],
    [ApplicationState.SALES_REVIEW]: [Action.VIEW_APPLICATION, Action.ACCEPT_TERMS, Action.COUNTER_TERMS, Action.REJECT_APPLICATION],
    [ApplicationState.APPROVED]: [Action.VIEW_APPLICATION, Action.MANDATE, Action.REJECT_APPLICATION],
    [ApplicationState.MANDATED]: [Action.VIEW_APPLICATION, Action.SIGN, Action.REJECT_APPLICATION],
    [ApplicationState.SIGNED]: [Action.VIEW_APPLICATION, Action.DISBURSE, Action.REJECT_APPLICATION],
    [ApplicationState.REJECTED]: [Action.VIEW_APPLICATION, Action.REOPEN_APPLICATION],
    [ApplicationState.DISBURSED]: [Action.VIEW_APPLICATION],
  },
};

// ─── Product-Type Permission Map ─────────────────────────────────────────────

export const PRODUCT_TYPE_PERMISSIONS: Record<ProductType, PermissionMatrix> = {
  [ProductType.PERSONAL_LOAN]: PERSONAL_LOAN_PERMISSIONS,
  [ProductType.BUSINESS_LOAN]: BUSINESS_LOAN_PERMISSIONS,
  [ProductType.CONSUMER_DURABLES_LOAN]: CONSUMER_DURABLES_LOAN_PERMISSIONS,
  [ProductType.GROUP_LOAN]: GROUP_LOAN_PERMISSIONS,
};

// ─── Default Role-Based Permissions (no application state context) ───────────

export const DEFAULT_ROLE_PERMISSIONS: DefaultPermissions = {
  [Role.SALES_USER]: [
    Action.CREATE_APPLICATION,
    Action.EDIT_APPLICATION,
    Action.SUBMIT_APPLICATION,
    Action.INVOICE_COLLECTED,
  ],
  [Role.BACK_OFFICE_USER]: [
    Action.SOFT_APPROVE,
    Action.REQUEST_EDIT_APPLICATION,
    Action.SANCTION,
    Action.MANDATE,
    Action.SIGN,
    Action.DISBURSE,
  ],
  [Role.FIELD_USER]: [],
  [Role.SALES_MANAGER]: [Action.VIEW_USER_LOGIN_HISTORY],
  [Role.ADMIN]: [Action.VIEW_USER_LOGIN_HISTORY],
  [Role.MANAGER]: [
    Action.CREATE_APPLICATION,
    Action.EDIT_APPLICATION,
    Action.SUBMIT_APPLICATION,
    Action.SOFT_APPROVE,
    Action.REQUEST_EDIT_APPLICATION,
    Action.SANCTION,
    Action.MANDATE,
    Action.SIGN,
    Action.DISBURSE,
    Action.INVOICE_COLLECTED,
    Action.VIEW_USER_LOGIN_HISTORY,
  ],
};

// ─── Feature-Level Access Control ────────────────────────────────────────────

export const FEATURE_ACCESS: FeatureAccessConfig = {
  [Role.SALES_USER]: ['dashboard', 'leads', 'applications', 'groups', 'tasks', 'residence-verification', 'settings', 'emi-calculator'],
  [Role.BACK_OFFICE_USER]: ['dashboard', 'leads', 'applications', 'groups', 'tasks', 'residence-verification', 'settings', 'emi-calculator'],
  [Role.SALES_MANAGER]: ['dashboard', 'leads', 'applications', 'groups', 'tasks', 'login-history', 'settings', 'emi-calculator'],
  [Role.ADMIN]: ['dashboard', 'leads', 'applications', 'groups', 'tasks', 'login-history', 'settings', 'emi-calculator', 'residence-verification'],
  [Role.FIELD_USER]: ['dashboard', 'residence-verification', 'settings'],
  [Role.MANAGER]: ['dashboard', 'leads', 'applications', 'groups', 'tasks', 'residence-verification', 'login-history', 'settings', 'emi-calculator'],
};

// ─── Residence Verification Editor Roles ─────────────────────────────────────
// Roles allowed to edit, save draft, complete, and upload photos for residence verification.
// BO users can VIEW but not take any action.

export const RV_EDITOR_ROLES: Role[] = [Role.SALES_USER, Role.FIELD_USER, Role.SALES_MANAGER, Role.ADMIN, Role.MANAGER];
